'use client';

import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,  } from 'recharts';

const data7j = [
  { date: '13 mars', visites: 2, planifiees: 1 },
  { date: '14 mars', visites: 4, planifiees: 2 },
  { date: '15 mars', visites: 1, planifiees: 3 },
  { date: '16 mars', visites: 3, planifiees: 2 },
  { date: '17 mars', visites: 5, planifiees: 1 },
  { date: '18 mars', visites: 2, planifiees: 3 },
  { date: '19 mars', visites: 3, planifiees: 3 },
];

const data30j = [
  { date: '18 fév', visites: 5, planifiees: 3 },
  { date: '22 fév', visites: 8, planifiees: 4 },
  { date: '26 fév', visites: 3, planifiees: 6 },
  { date: '1 mars', visites: 11, planifiees: 5 },
  { date: '5 mars', visites: 7, planifiees: 4 },
  { date: '9 mars', visites: 4, planifiees: 7 },
  { date: '13 mars', visites: 9, planifiees: 3 },
  { date: '17 mars', visites: 12, planifiees: 4 },
  { date: '19 mars', visites: 6, planifiees: 5 },
];

const data90j = [
  { date: 'Jan S1', visites: 14, planifiees: 8 },
  { date: 'Jan S2', visites: 9, planifiees: 11 },
  { date: 'Jan S3', visites: 18, planifiees: 7 },
  { date: 'Jan S4', visites: 12, planifiees: 9 },
  { date: 'Fév S1', visites: 21, planifiees: 12 },
  { date: 'Fév S2', visites: 16, planifiees: 14 },
  { date: 'Fév S3', visites: 8, planifiees: 10 },
  { date: 'Fév S4', visites: 24, planifiees: 11 },
  { date: 'Mar S1', visites: 19, planifiees: 13 },
  { date: 'Mar S2', visites: 11, planifiees: 16 },
];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl shadow-modal p-3 min-w-[160px]">
        <p className="text-xs font-600 text-slate-500 mb-2">{label}</p>
        {payload.map((p: any) => (
          <div key={p.name} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
              <span className="text-xs text-slate-600 capitalize">{p.name === 'visites' ? 'Réalisées' : 'Planifiées'}</span>
            </div>
            <span className="text-xs font-700 text-slate-900 tabular-nums">{p.value}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

interface Props {
  dateRange: string;
}

export default function VisitsChart({ dateRange }: Props) {
  const data = dateRange === '7j' ? data7j : dateRange === '30j' ? data30j : data90j;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-card">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-base font-600 text-slate-900">Activité des visites</h3>
          <p className="text-xs text-slate-400 mt-0.5">Visites réalisées vs planifiées</p>
        </div>
        <div className="flex items-center gap-4 text-xs text-slate-500">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-navy-700" />
            <span>Réalisées</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-accent-500/40" />
            <span>Planifiées</span>
          </div>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id="gradVisites" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#0F4C81" stopOpacity={0.15} />
              <stop offset="95%" stopColor="#0F4C81" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gradPlanifiees" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#E8A020" stopOpacity={0.15} />
              <stop offset="95%" stopColor="#E8A020" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: '#94a3b8', fontFamily: 'DM Sans' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#94a3b8', fontFamily: 'DM Sans' }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="visites"
            stroke="#0F4C81"
            strokeWidth={2}
            fill="url(#gradVisites)"
            dot={{ fill: '#0F4C81', strokeWidth: 0, r: 3 }}
            activeDot={{ r: 5, fill: '#0F4C81' }}
          />
          <Area
            type="monotone"
            dataKey="planifiees"
            stroke="#E8A020"
            strokeWidth={2}
            fill="url(#gradPlanifiees)"
            dot={{ fill: '#E8A020', strokeWidth: 0, r: 3 }}
            activeDot={{ r: 5, fill: '#E8A020' }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}